import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Send, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const HELPER = {
    trigger: "helper-trigger",
    tooltip: "helper-tooltip",
    panel: "helper-panel",
    closeBtn: "helper-close-btn",
    input: "helper-input",
    sendBtn: "helper-send-btn",
    micBtn: "helper-mic-btn",
    messages: "helper-messages",
    portrait: "helper-portrait",
};

/**
 * Floating "Ask me a question" helper widget.
 * - Persistent avatar (nerdy portrait with glasses) fixed bottom-right.
 * - Tooltip: "Press on me and ask me a question"
 * - Voice via MediaRecorder -> /api/ai/voice-transcribe (Whisper)
 * - Text via /api/ai/help-chat (Claude Sonnet 4.5, app-scoped)
 */
export default function HelperWidget() {
    const [open, setOpen] = useState(false);
    const [showTip, setShowTip] = useState(false);
    const [msgs, setMsgs] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const sessionRef = useRef(`h-${Date.now()}`);
    const scrollRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    // Auto-scroll to newest message
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [msgs, open]);

    // Show intro tooltip briefly on first mount
    useEffect(() => {
        const seen = sessionStorage.getItem("careco_helper_seen");
        if (seen) return;
        const t = setTimeout(() => setShowTip(true), 1200);
        const dismiss = setTimeout(() => {
            setShowTip(false);
            sessionStorage.setItem("careco_helper_seen", "1");
        }, 5500);
        return () => { clearTimeout(t); clearTimeout(dismiss); };
    }, []);

    async function sendText(q) {
        const msg = (q ?? text).trim();
        if (!msg || busy) return;
        setText("");
        setMsgs((m) => [...m, { role: "user", content: msg }]);
        setBusy(true);
        try {
            const { data } = await api.post("/ai/help-chat", {
                session_id: sessionRef.current,
                message: msg,
            });
            setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
        } catch (err) {
            setMsgs((m) => [...m, {
                role: "assistant",
                content: `Sorry — ${err?.response?.data?.detail || "the helper failed to reply."}`,
            }]);
        } finally {
            setBusy(false);
        }
    }

    async function startRecording() {
        if (recording) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : "audio/webm";
            const mr = new MediaRecorder(stream, { mimeType: mime });
            audioChunksRef.current = [];
            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            mr.onstop = async () => {
                const blob = new Blob(audioChunksRef.current, { type: mime });
                stream.getTracks().forEach((t) => t.stop());
                if (blob.size < 500) {
                    toast.error("Recording too short");
                    return;
                }
                setTranscribing(true);
                try {
                    const fd = new FormData();
                    fd.append("file", blob, "recording.webm");
                    const { data } = await api.post("/ai/voice-transcribe", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    const transcript = (data.text || "").trim();
                    if (!transcript) {
                        toast.error("Didn't catch that — try again");
                    } else {
                        sendText(transcript);
                    }
                } catch (err) {
                    toast.error(err?.response?.data?.detail || "Voice failed");
                } finally {
                    setTranscribing(false);
                }
            };
            mediaRecorderRef.current = mr;
            mr.start();
            setRecording(true);
        } catch (err) {
            toast.error("Microphone permission denied");
        }
    }

    function stopRecording() {
        if (!recording) return;
        try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
        setRecording(false);
    }

    function togglePanel() {
        setOpen((o) => !o);
        setShowTip(false);
        sessionStorage.setItem("careco_helper_seen", "1");
    }

    return (
        <div className="fixed bottom-20 right-6 z-[60] flex flex-col items-end gap-3 pointer-events-none">
            {/* Panel */}
            {open && (
                <div
                    data-testid={HELPER.panel}
                    className="pointer-events-auto w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-10rem)] bg-white rounded-3xl border border-stone-200 shadow-2xl flex flex-col overflow-hidden animate-fade-in"
                >
                    <div className="p-4 border-b border-stone-100 bg-gradient-to-r from-orange-50 via-white to-emerald-50 flex items-center gap-3">
                        <img
                            src="/assets/helper-portrait.png"
                            alt="Helper"
                            className="w-11 h-11 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="font-outfit font-semibold text-stone-900 text-sm">
                                Hi, I&rsquo;m your CareCo helper
                            </div>
                            <div className="text-[11px] text-stone-500 font-dmsans flex items-center gap-1">
                                <Sparkles className="w-3 h-3 text-emerald-600" />
                                Ask me anything about using the app
                            </div>
                        </div>
                        <button
                            data-testid={HELPER.closeBtn}
                            onClick={togglePanel}
                            aria-label="Close helper"
                            className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-500"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div
                        ref={scrollRef}
                        data-testid={HELPER.messages}
                        className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50"
                    >
                        {msgs.length === 0 && (
                            <div className="space-y-3">
                                <div className="text-sm text-stone-600 font-dmsans">
                                    Try one of these:
                                </div>
                                {[
                                    "How do I book care for my mom?",
                                    "What's the difference between packages and à la carte?",
                                    "Where do I set my EIN as an agency?",
                                    "How does clock-in and GPS work for caregivers?",
                                ].map((q) => (
                                    <button
                                        key={q}
                                        onClick={() => sendText(q)}
                                        className="w-full text-left text-sm px-4 py-2.5 rounded-2xl bg-white border border-stone-200 hover:border-emerald-400 hover:bg-emerald-50 text-stone-800 font-dmsans transition-colors"
                                    >
                                        {q}
                                    </button>
                                ))}
                            </div>
                        )}
                        {msgs.map((m, i) => (
                            <div
                                key={i}
                                className={`text-sm font-dmsans whitespace-pre-wrap ${
                                    m.role === "user"
                                        ? "ml-auto bg-emerald-700 text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[85%]"
                                        : "mr-auto bg-white border border-stone-200 text-stone-800 rounded-2xl rounded-bl-sm px-4 py-2 max-w-[85%]"
                                }`}
                            >
                                {m.content}
                            </div>
                        ))}
                        {(busy || transcribing) && (
                            <div className="mr-auto bg-white border border-stone-200 rounded-2xl rounded-bl-sm px-4 py-2 text-sm text-stone-400 font-dmsans flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                {transcribing ? "Transcribing…" : "Thinking…"}
                            </div>
                        )}
                    </div>

                    <form
                        onSubmit={(e) => { e.preventDefault(); sendText(); }}
                        className="p-3 border-t border-stone-100 flex items-center gap-2 bg-white"
                    >
                        <button
                            type="button"
                            data-testid={HELPER.micBtn}
                            onClick={recording ? stopRecording : startRecording}
                            disabled={busy || transcribing}
                            title={recording ? "Stop recording" : "Record voice"}
                            aria-label={recording ? "Stop recording" : "Record voice"}
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shrink-0 ${
                                recording
                                    ? "bg-red-600 text-white animate-pulse"
                                    : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                            }`}
                        >
                            {recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                        </button>
                        <input
                            data-testid={HELPER.input}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder={recording ? "Listening…" : "Type or press the mic"}
                            disabled={busy || transcribing || recording}
                            className="flex-1 h-10 rounded-full border border-stone-200 px-4 text-sm font-dmsans focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-stone-50"
                        />
                        <Button
                            data-testid={HELPER.sendBtn}
                            type="submit"
                            disabled={busy || transcribing || !text.trim()}
                            className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-10 w-10 p-0 shrink-0"
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </form>
                </div>
            )}

            {/* Tooltip bubble above the trigger */}
            {!open && showTip && (
                <div
                    data-testid={HELPER.tooltip}
                    className="pointer-events-auto max-w-[240px] bg-white rounded-2xl rounded-br-sm shadow-xl border border-stone-200 px-4 py-3 mr-1 animate-fade-in"
                >
                    <div className="text-sm text-stone-800 font-dmsans font-medium">
                        Press on me and ask me a question
                    </div>
                    <div className="text-[11px] text-stone-500 font-dmsans mt-0.5">
                        Voice or text — I know the app inside out.
                    </div>
                    {/* little tail */}
                    <div className="absolute -bottom-1.5 right-8 w-3 h-3 bg-white border-r border-b border-stone-200 rotate-45" />
                </div>
            )}

            {/* Portrait trigger */}
            <button
                data-testid={HELPER.trigger}
                onClick={togglePanel}
                aria-label="Open helper"
                className="pointer-events-auto group relative w-16 h-16 rounded-full shadow-lg hover:shadow-2xl transition-all hover:scale-105 focus:outline-none focus:ring-4 focus:ring-emerald-200"
            >
                <img
                    data-testid={HELPER.portrait}
                    src="/assets/helper-portrait.png"
                    alt="Ask the helper"
                    className="w-full h-full rounded-full object-cover border-[3px] border-white bg-emerald-50"
                    draggable={false}
                />
                {/* Green online dot */}
                <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full" />
                {/* Subtle pulse ring */}
                <span className="absolute inset-0 rounded-full border-2 border-emerald-400 opacity-0 group-hover:opacity-70 group-hover:animate-ping" />
            </button>
        </div>
    );
}
